import { useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  ApplicationButton,
  ApplicationPage,
  ApplicationStateRegion,
  ApplicationSurface,
  ApplicationViewHeader,
} from "../../components/application";
import { useAuth } from "../../auth/authContext";
import AVIcon from "../../components/icons/AllVoicedIcons";
import { appRegistry } from "../../data/appRegistry";
import { serviceRoutesById } from "../../navigation/publicServiceRoutes";
import useCatalogHome from "../hooks/useCatalogHome";

const RESOURCE_GUIDE_TITLE_ID =
  "catalog-resource-guide-title";

const resourceCorrespondences = [
  {
    slug: "2afc",
    launchConfigurationId: "overall-severity",
    previousLocation:
      "Learn → Discrimination (2AFC) → Overall severity",
    previousPath:
      "/app/view/discrimination-2afc-overall",
  },
  {
    slug: "2afc",
    launchConfigurationId: "roughness",
    previousLocation:
      "Learn → Discrimination (2AFC) → Roughness",
    previousPath:
      "/app/view/discrimination-2afc-roughness",
  },
  {
    slug: "2afc",
    launchConfigurationId: "breathiness",
    previousLocation:
      "Learn → Discrimination (2AFC) → Breathiness",
    previousPath:
      "/app/view/discrimination-2afc-breathiness",
  },
  {
    slug: "2afc",
    launchConfigurationId: "strain",
    previousLocation:
      "Learn → Discrimination (2AFC) → Strain",
    previousPath:
      "/app/view/discrimination-2afc-strain",
  },
  {
    slug: "2afc",
    launchConfigurationId: "random-attribute",
    previousLocation:
      "Learn → Discrimination (2AFC) → Random characteristic",
    previousPath:
      "/app/view/discrimination-2afc-random",
  },
  {
    slug: "guess-the-diagnosis",
    previousLocation:
      "Play → Guess the diagnosis",
    previousPath:
      "/app/view/guess-the-diagnosis",
  },
  {
    slug: "overall-severity",
    launchConfigurationId: "random-practice",
    previousLocation:
      "Practice → Overall severity → Get a random sample",
    previousPath:
      "/app/view/overall-severity-random-practice",
  },
  {
    slug: "overall-severity",
    launchConfigurationId: "select-practice",
    previousLocation:
      "Practice → Overall severity → Select a sample yourself",
    previousPath:
      "/app/view/overall-severity-select-practice",
  },
  {
    slug: "capev",
    launchConfigurationId: "random-practice",
    previousLocation:
      "Practice → CAPE-V → Get a random sample",
    previousPath:
      "/app/view/capev-random-practice",
  },
  {
    slug: "capev",
    launchConfigurationId: "select-practice",
    previousLocation:
      "Practice → CAPE-V → Select a sample yourself",
    previousPath:
      "/app/view/capev-select-practice",
  },
  {
    slug: "capevr",
    launchConfigurationId: "random-practice",
    previousLocation:
      "Practice → CAPE-Vr → Get a random sample",
    previousPath:
      "/app/view/capevr-random-practice",
  },
  {
    slug: "capevr",
    launchConfigurationId: "select-practice",
    previousLocation:
      "Practice → CAPE-Vr → Select a sample yourself",
    previousPath:
      "/app/view/capevr-select-practice",
  },
  {
    slug: "overall-severity",
    launchConfigurationId: "upload-sample",
    previousLocation:
      "Evaluate → Upload a sample → Overall severity",
    previousPath:
      "/app/view/evaluate-a-sample",
  },
  {
    slug: "capev",
    launchConfigurationId: "upload-sample",
    previousLocation:
      "Evaluate → Upload a sample → CAPE-V",
    previousPath:
      "/app/view/evaluate-a-sample",
  },
  {
    slug: "capevr",
    launchConfigurationId: "upload-sample",
    previousLocation:
      "Evaluate → Upload a sample → CAPE-Vr",
    previousPath:
      "/app/view/evaluate-a-sample",
  },
  {
    currentPath: "/app/facilitate/live",
    currentLabel:
      "Facilitate → Start a live session → Overall severity",
    previousLocation:
      "Interact → Moderate an interactive session → Overall severity",
    previousPath:
      "/app/view/overall-severity-session",
  },
  {
    currentPath: "/app/facilitate/live",
    currentLabel:
      "Facilitate → Start a live session → CAPE-V",
    previousLocation:
      "Interact → Moderate an interactive session → CAPE-V",
    previousPath:
      "/app/view/capev-session",
  },
  {
    currentPath: "/app/participate",
    currentLabel:
      "Participate → Join a live session",
    previousLocation:
      "Interact → Join an interactive session",
    previousPath:
      "/app/view/join-a-session",
  },
  {
    currentPath: "/app/participate",
    currentLabel:
      "Participate → Projects",
    previousLocation: "Projects",
    previousPath: "/app/projects",
  },
];

const newActivities = [
  {
    path: "/app/grbas/open",
    title: "GRBAS",
    description:
      "Practice GRBAS auditory-perceptual ratings with reference feedback.",
  },
];

const orientationSurfaceDefinitions = [
  {
    id: "dashboard",
    title: "Dashboard",
    description:
      "Your home base for continuing where you left off and getting back to the things you use most.",
    actionLabel: "Open Dashboard",
  },
  {
    id: "catalog",
    title: "Catalog",
    description:
      "Explore activities, resources, programs, and other offerings you want to use yourself.",
    actionLabel: "Open Catalog",
  },
  {
    id: "participate",
    title: "Participate",
    description:
      "Join activities or spaces that someone else has made available to you.",
    actionLabel: "Open Participate",
  },
  {
    id: "facilitate",
    title: "Facilitate",
    description:
      "Run activities with other people.",
    actionLabel: "Open Facilitate",
  },
  {
    id: "profile",
    title: "Profile",
    description:
      "Manage your account details and personal settings.",
    actionLabel: "Open Profile",
  },
];

const adminOrientationDefinition = {
  id: "admin",
  title: "Admin",
  description:
    "Access system administration and internal tools.",
  actionLabel: "Open Admin",
};

const availabilityCardDefinitions = [
  {
    id: "participate",
    title: "Participate",
    availableNow: [
      "Join live sessions",
      "Access your projects",
    ],
    comingSoonTitle: "Assigned work",
    comingSoonDescription:
      "Complete activities assigned to your account.",
  },
  {
    id: "facilitate",
    title: "Facilitate",
    availableNow: ["Start live sessions"],
    comingSoonTitle: "Assignments",
    comingSoonDescription:
      "Create and manage activities for specific participants.",
  },
];

function buildCatalogDetailLink(
  slug,
  launchConfigurationId
) {
  const params = new URLSearchParams();

  if (launchConfigurationId) {
    params.set("launchConfig", launchConfigurationId);
  }

  const query = params.toString();

  return `/app/catalog/${slug}${query ? `?${query}` : ""}`;
}

function getLaunchConfigurations(entry) {
  const configurations =
    entry?.metadata?.ux?.launchConfigurations;

  return Array.isArray(configurations)
    ? configurations
    : [];
}

function getLaunchConfiguration(
  entry,
  launchConfigurationId
) {
  if (!launchConfigurationId) {
    return null;
  }

  return (
    getLaunchConfigurations(entry).find(
      (configuration) =>
        configuration.id ===
        launchConfigurationId
    ) || null
  );
}

function ResourceGuidePageShell({ children }) {
  return (
    <ApplicationPage
      ariaLabelledby={RESOURCE_GUIDE_TITLE_ID}
      className="catalog-page app-catalog-page catalog-resource-guide"
      variant="discovery"
      width="standard"
    >
      {children}
    </ApplicationPage>
  );
}

export default function AppCatalogResourceGuidePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const {
    allEntries = [],
    loading,
    error,
  } = useCatalogHome();

  const navigationItemsById = useMemo(
    () =>
      new Map(
        appRegistry.map((item) => [item.id, item])
      ),
    []
  );

  const orientationSurfaces = useMemo(() => {
    const definitions = [
      ...orientationSurfaceDefinitions,
    ];

    if (user?.role === "admin") {
      definitions.push(adminOrientationDefinition);
    }

    return definitions.map((definition) => {
      const navigationItem =
        navigationItemsById.get(definition.id);

      return {
        ...definition,
        icon: navigationItem?.icon || "info",
        path:
          navigationItem?.route ||
          `/app/${definition.id}`,
      };
    });
  }, [navigationItemsById, user?.role]);

  const availabilityCards = useMemo(
    () =>
      availabilityCardDefinitions.map(
        (definition) => {
          const navigationItem =
            navigationItemsById.get(
              definition.id
            );

          return {
            ...definition,
            icon: navigationItem?.icon || "info",
          };
        }
      ),
    [navigationItemsById]
  );

  const entriesBySlug = useMemo(
    () =>
      new Map(
        allEntries.map((entry) => [entry.slug, entry])
      ),
    [allEntries]
  );

  const {
    resolvedCorrespondences,
    unresolvedCorrespondences,
  } = useMemo(() => {
    const resolved = [];
    const unresolved = [];

    resourceCorrespondences.forEach(
      (correspondence) => {
        if (correspondence.currentPath) {
          resolved.push({
            ...correspondence,
            entry: null,
            launchConfiguration: null,
          });
          return;
        }

        const entry = entriesBySlug.get(
          correspondence.slug
        );

        if (!entry) {
          unresolved.push({
            ...correspondence,
            reason: "missing-entry",
          });
          return;
        }

        const launchConfiguration =
          getLaunchConfiguration(
            entry,
            correspondence.launchConfigurationId
          );

        if (
          correspondence.launchConfigurationId &&
          !launchConfiguration
        ) {
          unresolved.push({
            ...correspondence,
            reason: "missing-launch-configuration",
          });
          return;
        }

        resolved.push({
          ...correspondence,
          entry,
          launchConfiguration,
        });
      }
    );

    return {
      resolvedCorrespondences: resolved,
      unresolvedCorrespondences: unresolved,
    };
  }, [entriesBySlug]);

  useEffect(() => {
    if (
      process.env.NODE_ENV !== "production" &&
      !loading &&
      !error &&
      unresolvedCorrespondences.length > 0
    ) {
      console.warn(
        "[Catalog resource guide] Unresolved catalog correspondences:",
        unresolvedCorrespondences.map(
          ({ slug, launchConfigurationId, reason }) => ({
            slug,
            launchConfigurationId:
              launchConfigurationId || null,
            reason,
          })
        )
      );
    }
  }, [error, loading, unresolvedCorrespondences]);

  useEffect(() => {
    if (loading || error || !location.hash) {
      return undefined;
    }

    const targetId = location.hash.replace(/^#/, "");

    if (!targetId) {
      return undefined;
    }

    let animationFrameId = null;
    let timeoutId = null;

    const scrollToHashTarget = () => {
      const target = document.getElementById(targetId);

      if (target) {
        target.scrollIntoView();
      }
    };

    animationFrameId = window.requestAnimationFrame(() => {
      scrollToHashTarget();
      timeoutId = window.setTimeout(scrollToHashTarget, 0);
    });

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [error, loading, location.hash]);

  if (loading) {
    return (
      <ResourceGuidePageShell>
        <ApplicationViewHeader
          title="Getting oriented in All-Voiced"
          titleId={RESOURCE_GUIDE_TITLE_ID}
          contextLabel="All-Voiced guide"
          className="catalog-resource-guide__header"
        />

        <ApplicationStateRegion
          status="loading"
          message="Loading..."
        />
      </ResourceGuidePageShell>
    );
  }

  if (error) {
    return (
      <ResourceGuidePageShell>
        <ApplicationViewHeader
          title="Getting oriented in All-Voiced"
          titleId={RESOURCE_GUIDE_TITLE_ID}
          contextLabel="All-Voiced guide"
          className="catalog-resource-guide__header"
        />

        <ApplicationStateRegion
          status="error"
          message={error}
        />
      </ResourceGuidePageShell>
    );
  }

  return (
    <ResourceGuidePageShell>
      <header className="catalog-resource-guide__header">
        <p className="catalog-hero__eyebrow">
          All-Voiced guide
        </p>

        <h1 id={RESOURCE_GUIDE_TITLE_ID}>
          Getting oriented in All-Voiced
        </h1>

        <p>
          New to All-Voiced or returning after
          the recent changes? This guide shows
          you where to go, what you can do now,
          and where familiar activities and
          features have moved.
        </p>
      </header>

      <section
        className="catalog-resource-guide__section"
        aria-labelledby="catalog-resource-guide-orientation"
      >
      <h2 id="catalog-resource-guide-orientation">
        Where should I go?
      </h2>

      <p>
        Use the sidebar to move between the main areas of
        All-Voiced. The cards below explain what each
        section is for.
      </p>

      <div className="catalog-resource-guide__card-grid">
          {orientationSurfaces.map(
            ({
              path,
              title,
              description,
              actionLabel,
              icon,
            }) => (
              <ApplicationSurface
                key={path}
                className="catalog-resource-guide__card"
              >
                <div className="catalog-resource-guide__card-heading">
                  <span
                    aria-hidden="true"
                    className="av-app-icon-badge catalog-resource-guide__card-icon"
                  >
                    <AVIcon icon={icon} size={20} />
                  </span>
                  <h3>{title}</h3>
                </div>
                <p>{description}</p>
                <ApplicationButton
                  onClick={() => navigate(path)}
                  variant="secondary"
                >
                  {actionLabel}
                </ApplicationButton>
              </ApplicationSurface>
            )
          )}
        </div>
      </section>

      <section
        className="catalog-resource-guide__section"
        aria-labelledby="catalog-resource-guide-availability"
      >
        <h2 id="catalog-resource-guide-availability">
          Current availability
        </h2>

        <div className="catalog-resource-guide__availability-grid">
          {availabilityCards.map(
            ({
              id,
              title,
              icon,
              availableNow,
              comingSoonTitle,
              comingSoonDescription,
            }) => (
              <ApplicationSurface
                key={id}
                className="catalog-resource-guide__availability-card"
                aria-labelledby={`catalog-resource-guide-availability-${id}`}
              >
                <div className="catalog-resource-guide__card-heading">
                  <span
                    aria-hidden="true"
                    className="av-app-icon-badge catalog-resource-guide__card-icon"
                  >
                    <AVIcon icon={icon} size={20} />
                  </span>
                  <h3
                    id={`catalog-resource-guide-availability-${id}`}
                  >
                    {title}
                  </h3>
                </div>
                <p className="catalog-resource-guide__status-label">
                  <strong>Available now</strong>
                </p>
                <ul className="catalog-resource-guide__status-list">
                  {availableNow.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="catalog-resource-guide__status-label catalog-resource-guide__status-label--secondary">
                  <strong>Coming soon</strong>
                </p>
                <ul className="catalog-resource-guide__status-list">
                  <li>{comingSoonTitle}</li>
                </ul>
                <p className="catalog-resource-guide__availability-description">
                  {comingSoonDescription}
                </p>
              </ApplicationSurface>
            )
          )}
        </div>
      </section>

      <section
        className="catalog-resource-guide__section"
        aria-labelledby="catalog-resource-guide-feedback"
      >
        <h2 id="catalog-resource-guide-feedback">
          Clearer feedback and comparisons
        </h2>

        <ApplicationSurface
          as="div"
          className="catalog-resource-guide__feature"
          variant="subtle"
        >
          <div
            aria-hidden="true"
            className="av-app-icon-badge catalog-resource-guide__feature-icon"
          >
            <AVIcon icon="info" size={24} />
          </div>

          <div className="catalog-resource-guide__feature-copy">
          <p>
            We are also refining how feedback is presented across
            All-Voiced. When you see ratings from other All-Voiced
            users or from external data sources, the interface gives
            clearer information about where those ratings come from
            and how they relate to your own evaluation.
          </p>

          <p>
            Different sources and rating approaches are kept
            distinguishable rather than being treated as automatically
            equivalent. This is intended to make comparisons easier to
            understand and more methodologically transparent.
          </p>
          </div>
        </ApplicationSurface>
      </section>

      <section
        className="catalog-resource-guide__section"
        aria-labelledby="catalog-resource-guide-projects"
      >
        <h2 id="catalog-resource-guide-projects">
          Projects in All-Voiced
        </h2>

        <ApplicationSurface
          as="div"
          className="catalog-resource-guide__project-introduction"
          variant="subtle"
        >
          <div
            aria-hidden="true"
            className="av-app-icon-badge catalog-resource-guide__feature-icon"
          >
            <AVIcon icon="team" size={24} />
          </div>

          <div className="catalog-resource-guide__feature-copy">
            <p>
              Projects are tailored All-Voiced spaces
              configured for a specific course, research
              study, perceptual data initiative, or
              institutional workflow. They can bring together
              the activities, participants, access, and
              structured workflows needed for that context.
            </p>

            <p className="catalog-resource-guide__route-callout">
              If you belong to a Project, open{" "}
              <Link to="/app/participate">
                Participate
              </Link>{" "}
              <span aria-hidden="true">→</span>{" "}
              <strong>Projects</strong> to enter it.
            </p>
          </div>
        </ApplicationSurface>

        <div className="catalog-resource-guide__project-links">
          <h3>
            Explore how Projects can be used
          </h3>

          <ul className="catalog-resource-guide__project-use-grid">
            <li>
              <Link
                to={serviceRoutesById["institutional-programs"].path}
              >
                <AVIcon icon="graduationCap" size={20} />
                Institutional Programs
              </Link>
            </li>
            <li>
              <Link
                to={
                  serviceRoutesById[
                    "perceptual-research-infrastructure"
                  ].path
                }
              >
                <AVIcon icon="research" size={20} />
                Perceptual Research Infrastructure
              </Link>
            </li>
            <li>
              <Link
                to={
                  serviceRoutesById[
                    "perceptual-data-annotation"
                  ].path
                }
              >
                <AVIcon icon="tags" size={20} />
                Perceptual Data Annotation
              </Link>
            </li>
          </ul>

          <p className="catalog-resource-guide__project-contact">
            Interested in creating an All-Voiced Project
            for your course, research study, or
            institutional workflow?{" "}
            <Link to="/...">
              Get in touch
            </Link>
            .
          </p>
        </div>
      </section>

      <section
        className="catalog-resource-guide__section"
        aria-labelledby="catalog-resource-guide-returning"
      >
        <h2 id="catalog-resource-guide-returning">
          Used All-Voiced before?
        </h2>

        <p>
          The navigation has changed, but familiar
          activities and workflows are still
          available. Here are the main
          differences.
        </p>

        <ul className="catalog-resource-guide__migration-list">
          <li>
            Practice and evaluation modes for the same activity are now grouped together.
          </li>
          <li>
            Live sessions you join are under <strong>Participate</strong>.
          </li>
          <li>
            Live sessions you run are under <strong>Facilitate</strong> or <strong>Use with others</strong> from a supported activity.
          </li>
          <li>
            Projects are accessed through <strong>Participate</strong>.
          </li>
          <li>
            Languages and adaptations are selected within the relevant activity when available.
          </li>
          <li>
            Favorites remain under <strong>Saved</strong>, and entries can still be pinned to the Dashboard.
          </li>
        </ul>

        <div className="catalog-resource-guide__table-introduction">
          <h3 id="catalog-resource-guide-previous">
            Where did familiar activities move?
          </h3>

          <p>
            The table below shows where activities
            and resources from the previous
            All-Voiced navigation can now be found.
            Previous URLs are shown as reference
            text so you can update links in course
            materials or documentation.
          </p>
        </div>

        <div className="catalog-resource-guide__table-wrapper">
          <table className="catalog-resource-guide__table">
            <thead>
              <tr>
                <th scope="col">
                  Previously found under
                </th>
                <th scope="col">Previous URL</th>
                <th scope="col">Find it now</th>
              </tr>
            </thead>

            <tbody>
              {resolvedCorrespondences.map(
                ({
                  entry,
                  launchConfiguration,
                  launchConfigurationId,
                  currentPath,
                  currentLabel,
                  previousLocation,
                  previousPath,
                }) => {
                  const currentDestination =
                    currentPath ||
                    buildCatalogDetailLink(
                      entry.slug,
                      launchConfigurationId
                    );

                  const currentDestinationLabel =
                    currentLabel ||
                    [
                      entry.title,
                      launchConfiguration?.label,
                    ]
                      .filter(Boolean)
                      .join(" — ");

                  return (
                    <tr
                      key={[
                        previousPath,
                        entry?.slug ||
                          currentPath ||
                          "direct",
                        launchConfigurationId ||
                          "default",
                      ].join(":")}
                    >
                      <td data-label="Previously found under">
                        {previousLocation}
                      </td>

                      <td data-label="Previous URL">
                        <code>{previousPath}</code>
                      </td>

                      <td data-label="Find it now">
                        <Link to={currentDestination}>
                          {currentDestinationLabel}
                        </Link>
                      </td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </table>
        </div>

        {resolvedCorrespondences.length === 0 && (
          <ApplicationStateRegion
            status="empty"
            message="No matching catalog entries are currently available."
          />
        )}
      </section>

      {newActivities.length > 0 && (
        <section
          className="catalog-resource-guide__section"
          aria-labelledby="catalog-resource-guide-new-activities"
        >
          <h2 id="catalog-resource-guide-new-activities">
            New activities
          </h2>

          <p>
            We are also expanding the range of perceptual activities
            available through All-Voiced. GRBAS practice is one of the
            first additions in this new version, with more activities
            to follow over time.
          </p>

          <div className="catalog-resource-guide__new-activity-grid">
            {newActivities.map(
              ({ path, title, description }) => (
                <ApplicationSurface
                  as="article"
                  className="catalog-resource-guide__new-activity-card"
                  key={path}
                  variant="action"
                >
                  <div
                    aria-hidden="true"
                    className="av-app-icon-badge catalog-resource-guide__feature-icon"
                  >
                    <AVIcon icon="listen" size={24} />
                  </div>
                  <div className="catalog-resource-guide__feature-copy">
                    <h3>
                      <Link to={path}>{title}</Link>
                    </h3>
                    {description && <p>{description}</p>}
                  </div>
                </ApplicationSurface>
              )
            )}
          </div>
        </section>
      )}
    </ResourceGuidePageShell>
  );
}
